import { DevToolsCopyBox } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsCopyBox';
import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import { DevToolsStatusBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsStatusBadge';
import type { DevToolsBadgeVariant } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsStatusBadge/index.types';
import type { CallsConferenceStatusProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/CallsTab/CallsConferenceStatus/index.types';
import {
  TONE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { formatTimestamp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import React from 'react';
import { cn } from '~/lib/utils';

const CONFERENCE_STATE_VARIANTS: Record<string, DevToolsBadgeVariant> = {
  active: 'success',
  establishing: 'warning',
};

/**
 * CallsConferenceStatus - Merge progress and the live conference room
 *
 * The participant list is cross-checked against `activeCalls`, because a leg
 * named by the room but missing from the call map is exactly what a
 * half-joined merge looks like — and the conference server will not tell us,
 * since it emits no join or leave events.
 */
export const CallsConferenceStatus: React.FC<CallsConferenceStatusProps> = ({
  isMerging,
  activeConference,
  activeCalls,
  currentCallId,
}) => {
  if (!isMerging && !activeConference) {
    return null;
  }

  return (
    <div className={TONE.warn.card}>
      <div className={cn('flex items-center gap-2', TONE.warn.header)}>
        <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-amber-400">
          Conference
        </span>
        <div className="flex-1" />
        {isMerging && (
          <span className="flex items-center gap-1.5 font-sp-mono text-[10px] text-amber-400">
            <span className="h-[5px] w-[5px] animate-pulse rounded-full bg-amber-400" />
            merging…
          </span>
        )}
      </div>

      {activeConference && (
        <div className="flex flex-col gap-2.5 p-3">
          <div className="flex items-center gap-2">
            <DevToolsStatusBadge
              label={activeConference.conferenceState}
              variant={
                CONFERENCE_STATE_VARIANTS[activeConference.conferenceState] ??
                'error'
              }
            />
            <span className="text-[11.5px] text-zinc-300">
              {activeConference.participantIds.length} participants
            </span>
            <div className="flex-1" />
            <span className="font-sp-mono text-[10.5px] text-zinc-400">
              {formatTimestamp(activeConference.startTime, { fallback: '—' })}
            </span>
          </div>

          <DevToolsCopyBox value={activeConference.roomGuid} label="Room GUID" />
          <DevToolsCopyBox value={activeConference.id} label="Conference ID" />

          <div>
            <DevToolsMicroLabel className="mb-1.5">
              Participant calls
            </DevToolsMicroLabel>
            <div className="flex flex-col gap-1">
              {activeConference.participantIds.map((id) => {
                const tone = activeCalls.has(id) ? TONE.ok : TONE.muted;

                return (
                  <div key={id} className="flex items-center gap-2">
                    <span
                      className={cn(
                        'h-1.5 w-1.5 flex-shrink-0 rounded-full',
                        tone.dot
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate font-sp-mono text-[11px] text-zinc-300">
                      {id}
                    </span>
                    {id === currentCallId && (
                      <DevToolsStatusBadge label="current" variant="primary" />
                    )}
                  </div>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10.5px] leading-[1.5] text-zinc-400">
              Inferred client-side — the conference server emits no join or
              leave events.
            </p>
          </div>

          <div className="flex items-center gap-2 border-t border-zinc-800 pt-2.5">
            <DevToolsMicroLabel>Room session</DevToolsMicroLabel>
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                activeConference.roomSession ? TONE.ok.dot : TONE.muted.dot
              )}
            />
            <span className="text-[11px] text-zinc-300">
              {activeConference.roomSession ? 'Active' : 'None'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
